import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles, Heart, Target, Compass, User, LogOut, Crown, History, Leaf, MessageCircle, BookOpen, Map } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { AuthModal } from "../components/AuthModal";
import { useAuth } from "../contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const isFrench = i18n.language === 'fr';

  const diagnostics = [
    {
      id: "epanoui",
      icon: <Sparkles className="w-8 h-8" />,
      title: isFrench ? "Blocages émotionnels" : "Emotional blocks",
      subtitle: isFrench ? "Diagnostic Épanoui" : "Épanoui Diagnostic",
      description: isFrench 
        ? "Identifie tes blocages intérieurs et reçois des conseils personnalisés pour t'épanouir pleinement."
        : "Identify your inner blocks and receive personalized advice to fully flourish.",
      color: "from-[#2F4F4F] to-[#3D6363]",
      accent: "#E2725B",
      link: "/diagnostic",
      features: [
        isFrench ? "10 questions ciblées" : "10 targeted questions",
        isFrench ? "Analyse IA personnalisée" : "Personalized AI analysis",
        isFrench ? "Exercices pratiques" : "Practical exercises"
      ]
    },
    {
      id: "emergence",
      icon: <Leaf className="w-8 h-8" />,
      title: isFrench ? "Accompagnement au changement" : "Change support",
      subtitle: isFrench ? "Diagnostic de la Transformation" : "Transformation Diagnostic",
      description: isFrench 
        ? "Traverse ta période de transition avec sérénité. Que tu vives une reconversion professionnelle, un épuisement, une séparation, ou l'envie d'un nouveau projet de vie."
        : "Navigate your transition period with serenity. Whether you're experiencing a career change, exhaustion, separation, or the desire for a new life project.",
      color: "from-[#3B7A8C] to-[#5BA3B0]",
      accent: "#D4A574",
      link: "/emergence/diagnostic",
      features: [
        isFrench ? "Reconversion ou évolution pro" : "Career change or evolution",
        isFrench ? "Épuisement, perte de sens" : "Exhaustion, loss of meaning",
        isFrench ? "Confiance et affirmation de soi" : "Confidence and self-assertion"
      ]
    },
    {
      id: "separation",
      icon: <Heart className="w-8 h-8" />,
      title: isFrench ? "Après la séparation" : "After separation",
      subtitle: isFrench ? "Diagnostic Reconstruction" : "Reconstruction Diagnostic",
      description: isFrench 
        ? "Après une séparation, on ne souffre pas seulement de la rupture. On souffre de ne plus savoir où est sa place dans un monde qui a changé."
        : "After a separation, we don't just suffer from the breakup. We suffer from no longer knowing where our place is in a world that has changed.",
      color: "from-[#8B5A6B] to-[#A67B8B]",
      accent: "#E8B4B8",
      link: "/separation/diagnostic",
      features: [
        isFrench ? "Reconstruction identitaire" : "Identity reconstruction",
        isFrench ? "Solitude & place sociale" : "Loneliness & social place",
        isFrench ? "Confiance & projection" : "Confidence & projection"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAF9F6] to-[#F0F4F3]">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-gray-100/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Sparkles className="w-6 h-6 text-[#2F4F4F]" />
            <span className="font-heading text-xl font-semibold text-[#2F4F4F]">Épanoui</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/exercises" data-testid="nav-exercises">
              <Button variant="ghost" className="text-[#2F4F4F] hover:bg-[#2F4F4F]/5">
                {t('nav.exercises')}
              </Button>
            </Link>
            <Link to="/emergence/parcours" data-testid="nav-parcours">
              <Button variant="ghost" className="text-[#3B7A8C] hover:bg-[#3B7A8C]/5">
                {isFrench ? "Parcours 21j" : "21-Day Journeys"}
              </Button>
            </Link>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-full border-gray-200 gap-2" data-testid="user-menu">
                    {user.is_premium && <Crown className="w-4 h-4 text-[#E2725B]" />}
                    <User className="w-4 h-4" />
                    {user.name}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="text-[#5C5C5C]">
                    {user.is_premium ? (
                      <span className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-[#E2725B]" />
                        Premium
                      </span>
                    ) : (
                      user.free_diagnostic_used ? t('auth.trialUsed') : t('auth.freeTrial')
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <Link to="/history">
                    <DropdownMenuItem data-testid="history-link">
                      <History className="w-4 h-4 mr-2" />
                      {isFrench ? 'Mon historique' : 'My history'}
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/emergence/journal">
                    <DropdownMenuItem>
                      <BookOpen className="w-4 h-4 mr-2" />
                      {isFrench ? 'Mon journal' : 'My journal'}
                    </DropdownMenuItem>
                  </Link>
                  {!user.is_premium && (
                    <Link to="/premium">
                      <DropdownMenuItem className="text-[#E2725B]" data-testid="premium-link">
                        <Crown className="w-4 h-4 mr-2" />
                        {isFrench ? 'Passer à Premium' : 'Upgrade to Premium'}
                      </DropdownMenuItem>
                    </Link>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-500" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                variant="ghost" 
                className="text-[#2F4F4F] hover:bg-[#2F4F4F]/5"
                onClick={() => setAuthModalOpen(true)}
                data-testid="login-btn"
              >
                {t('nav.login')}
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="animate-fade-in-up opacity-0 stagger-1">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#2F4F4F]/10 to-[#3B7A8C]/10 text-[#2F4F4F] text-sm font-medium mb-6">
              <Heart className="w-4 h-4" />
              {isFrench ? "Accompagnement personnalisé par IA" : "AI-powered personal support"}
            </span>
          </div>
          
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2D2D2D] leading-tight mb-6 animate-fade-in-up opacity-0 stagger-2">
            {isFrench ? (
              <>Ton parcours vers <em className="text-[#E2725B] not-italic">l'épanouissement</em></>
            ) : (
              <>Your journey to <em className="text-[#E2725B] not-italic">fulfillment</em></>
            )}
          </h1>
          
          <p className="text-lg text-[#5C5C5C] max-w-2xl mx-auto leading-relaxed mb-12 animate-fade-in-up opacity-0 stagger-3">
            {isFrench 
              ? "Diagnostics personnalisés, coaching IA bienveillant, et parcours guidés pour t'accompagner dans ta transformation personnelle."
              : "Personalized diagnostics, caring AI coaching, and guided journeys to support your personal transformation."}
          </p>
        </div>
      </section>

      {/* Diagnostic Cards */}
      <section className="pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {diagnostics.map((diag, idx) => (
              <Card 
                key={diag.id}
                className="group overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300 animate-fade-in-up opacity-0"
                style={{ animationDelay: `${0.4 + idx * 0.1}s` }}
                data-testid={`diagnostic-card-${diag.id}`}
              >
                <div className={`h-2 bg-gradient-to-r ${diag.color}`} />
                <CardContent className="p-8">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${diag.color} text-white flex items-center justify-center mb-6 group-hover:scale-105 transition-transform`}>
                    {diag.icon}
                  </div>
                  
                  <p className="text-sm font-medium mb-2" style={{ color: diag.accent }}>
                    {diag.subtitle}
                  </p>
                  
                  <h2 className="font-heading text-2xl font-semibold text-[#2D2D2D] mb-3">
                    {diag.title}
                  </h2>
                  
                  <p className="text-[#5C5C5C] leading-relaxed mb-6">
                    {diag.description}
                  </p>
                  
                  <ul className="space-y-2 mb-8">
                    {diag.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#5C5C5C]">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: diag.accent }} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Link to={diag.link} data-testid={`start-${diag.id}-btn`}>
                    <Button 
                      className={`w-full rounded-full py-6 text-white bg-gradient-to-r ${diag.color} hover:opacity-90 transition-opacity`}
                    >
                      {isFrench ? "Commencer le diagnostic" : "Start diagnostic"}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl font-semibold text-[#2D2D2D] mb-4">
              {isFrench ? "Un accompagnement complet" : "Complete support"}
            </h2>
            <p className="text-[#5C5C5C] max-w-xl mx-auto">
              {isFrench 
                ? "Tous les outils pour ta transformation personnelle, réunis en un seul endroit."
                : "All the tools for your personal transformation, gathered in one place."}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard 
              icon={<Compass className="w-6 h-6" />}
              title={isFrench ? "Diagnostics IA" : "AI Diagnostics"}
              description={isFrench ? "Analyse personnalisée de ta situation" : "Personalized analysis of your situation"}
              color="#2F4F4F"
            />
            <FeatureCard 
              icon={<MessageCircle className="w-6 h-6" />}
              title={isFrench ? "Coach intérieur" : "Inner Coach"}
              description={isFrench ? "Chat bienveillant disponible 24/7" : "Caring chat available 24/7"}
              color="#3B7A8C"
            />
            <FeatureCard 
              icon={<Map className="w-6 h-6" />}
              title={isFrench ? "Parcours 21 jours" : "21-Day Journeys"}
              description={isFrench ? "Programmes guidés étape par étape" : "Step-by-step guided programs"}
              color="#D4A574"
            />
            <FeatureCard 
              icon={<BookOpen className="w-6 h-6" />}
              title={isFrench ? "Journal intelligent" : "Smart Journal"}
              description={isFrench ? "Réflexions et prises de conscience" : "Reflections and insights"}
              color="#E2725B"
            />
          </div>
        </div>
      </section>

      {/* Parcours Premium Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <Card className="overflow-hidden border-0 shadow-xl bg-gradient-to-br from-[#3B7A8C] to-[#2D5A66]">
            <CardContent className="p-10 text-center text-white">
              <Leaf className="w-12 h-12 mx-auto mb-6 text-[#D4A574]" />
              <h2 className="font-heading text-3xl font-semibold mb-4">
                {isFrench ? "Parcours guidés Premium" : "Premium Guided Journeys"}
              </h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                {isFrench 
                  ? "3 parcours de 21 jours pour t'accompagner dans ta transformation : retrouver du sens, sortir de l'épuisement, oser changer."
                  : "3 twenty-one day journeys to support your transformation: finding meaning, overcoming exhaustion, daring to change."}
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <span className="px-4 py-2 rounded-full bg-white/10 text-sm">🧭 {isFrench ? "Retrouver du sens" : "Finding meaning"}</span>
                <span className="px-4 py-2 rounded-full bg-white/10 text-sm">🔥 {isFrench ? "Sortir de l'épuisement" : "Overcoming exhaustion"}</span>
                <span className="px-4 py-2 rounded-full bg-white/10 text-sm">🦋 {isFrench ? "Oser changer" : "Daring to change"}</span>
              </div>
              <Link to="/emergence/parcours" data-testid="parcours-cta">
                <Button className="rounded-full bg-[#D4A574] hover:bg-[#D4A574]/90 text-white px-8 py-6 text-lg">
                  {isFrench ? "Découvrir les parcours" : "Discover journeys"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl font-semibold text-[#2D2D2D] mb-4">
            {isFrench ? "Prêt(e) à commencer ?" : "Ready to begin?"}
          </h2>
          <p className="text-[#5C5C5C] mb-8">
            {isFrench 
              ? "Le premier diagnostic est gratuit. Découvre où tu en es et reçois des conseils personnalisés."
              : "The first diagnostic is free. Discover where you are and receive personalized advice."}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/diagnostic" data-testid="cta-epanoui">
              <Button className="rounded-full bg-[#2F4F4F] hover:bg-[#2F4F4F]/90 text-white px-8 py-6">
                <Sparkles className="w-5 h-5 mr-2" />
                {isFrench ? "Diagnostic blocages" : "Blocks diagnostic"}
              </Button>
            </Link>
            <Link to="/emergence/diagnostic" data-testid="cta-emergence">
              <Button className="rounded-full bg-[#3B7A8C] hover:bg-[#3B7A8C]/90 text-white px-8 py-6">
                <Leaf className="w-5 h-5 mr-2" />
                {isFrench ? "Diagnostic changement" : "Change diagnostic"}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-200/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#2F4F4F]" />
            <span className="font-heading text-lg text-[#2F4F4F]">Épanoui</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-[#8A8A8A]">
            <Link to="/exercises" className="hover:text-[#2F4F4F]">{t('nav.exercises')}</Link>
            <Link to="/emergence/chat" className="hover:text-[#3B7A8C]">{isFrench ? "Coach IA" : "AI Coach"}</Link>
            <Link to="/emergence/parcours" className="hover:text-[#3B7A8C]">{isFrench ? "Parcours" : "Journeys"}</Link>
          </div>
          <p className="text-sm text-[#8A8A8A]">
            © 2025 Épanoui. {t('landing.footer')}
          </p>
        </div>
      </footer>

      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </div>
  );
}

function FeatureCard({ icon, title, description, color }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow">
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 text-white"
        style={{ backgroundColor: color }}
      >
        {icon}
      </div>
      <h3 className="font-heading text-lg font-semibold text-[#2D2D2D] mb-2">{title}</h3>
      <p className="text-sm text-[#5C5C5C]">{description}</p>
    </div>
  );
}
